import React, { useEffect, useState, useMemo } from "react";
import { Badge, Spinner, Modal, Button } from "react-bootstrap";
import axios from "axios";
import api from "../../api/axios";

export default function StarRating({
  benefitId,
  placement = "br",
  fallbackAvg = 4.5,
  showFallbackIfEmpty = true,
  limit = 10, // cantidad por página en el modal
}) {
  const [avg, setAvg] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [usedFallback, setUsedFallback] = useState(false);

  // Modal state
  const [show, setShow] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listEnd, setListEnd] = useState(false);

  // --- Summary (promedio y total) ---
  useEffect(() => {
    let mounted = true;

    async function fetchSummary() {
      try {
        setLoading(true);
        setErr("");

        const url1 = `/reviews/benefit/${benefitId}`;
        try {
          const { data } = await api.get(url1, { params: { limit: 0 } });
          if (!mounted) return;

          const avgRating = Number(data?.avg_rating || 0);
          const count = Number(data?.total || 0);

          setAvg(avgRating);
          setTotal(count);
          setUsedFallback(false);
          return;
        } catch (e1) {
          const code = e1?.response?.status;
          if (code && code !== 404) throw e1;
        }

        // Fallback directo por si tu baseURL no encaja
        const base2 = "http://localhost:4000";
        const url2 = `${base2}/reviews/benefit/${benefitId}?limit=0`;
        const { data } = await axios.get(url2);
        if (!mounted) return;

        setAvg(Number(data?.avg_rating || 0));
        setTotal(Number(data?.total || 0));
        setUsedFallback(true);
      } catch (e) {
        if (!mounted) return;
        setErr("x");
        setAvg(0);
        setTotal(0);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    if (benefitId != null) fetchSummary();
    return () => { mounted = false; };
  }, [benefitId]);

  const useFallback = showFallbackIfEmpty && total === 0;
  const displayAvg = useFallback ? fallbackAvg : avg;

  const pos = useMemo(() => ({
    tl: { top: 8, left: 8 },
    tr: { top: 8, right: 8 },
    bl: { bottom: 8, left: 8 },
    br: { bottom: 8, right: 8 },
  }[placement] || { bottom: 8, right: 8 }), [placement]);

  const badgeText = loading
    ? "Cargando…"
    : err
      ? "—"
      : `${displayAvg ? displayAvg.toFixed(1) : "—"}★${useFallback ? " · Nuevo" : ` (${total})`}${usedFallback ? " · alt" : ""}`;

  // --- Modal: cargar reseñas paginadas ---
  const openModal = async () => {
    setShow(true);
    setItems([]);
    setPage(1);
    setListEnd(false);
    await loadPage(1);
  };

useEffect(() => {
  if (show) {
     loadPage(1, { force: true });
   }
 }, [show, benefitId]); // si cambia el beneficio, recarga desde 1


  const loadPage = async (p) => {
    if (listLoading || listEnd) return;
    try {
      setListLoading(true);
      const { data } = await api.get(`/reviews/benefit/${benefitId}`, {
        params: { page: p, limit }
      });
      const arr = Array.isArray(data?.items) ? data.items : [];
      setItems(prev => p === 1 ? arr : [...prev, ...arr]);

      // si trajo menos que el límite, asumimos fin
      if (arr.length < limit) setListEnd(true);

      // también refrescamos summary si vino en la respuesta
      if (typeof data?.avg_rating !== "undefined") setAvg(Number(data.avg_rating || 0));
      if (typeof data?.total !== "undefined") setTotal(Number(data.total || 0));
    } catch (e) {
      // noop: si falla, dejamos la UI como está
    } finally {
      setListLoading(false);
      setPage(p);
    }
  };

  const loadMore = () => loadPage(page + 1);

  const renderReviewer = (it) => {
    const name = it?.user_name || `Usuario #${it?.user_id || ""}`;
    const avatar = it?.user_avatar_url || null;
    const initial = String(name || "U").charAt(0).toUpperCase();
    return (
      <div className="d-flex align-items-center gap-2 mb-1">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              objectFit: "cover",
              border: "1px solid #e2e8f0",
            }}
          />
        ) : (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: "#111827",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {initial}
          </div>
        )}
        <span className="text-muted" style={{ fontSize: ".9rem" }}>
          {name}
        </span>
      </div>
    );
  };

  return (
    <>
      <div style={{ position: "absolute", zIndex: 2, ...pos }}>
        {loading ? (
          <Badge bg="light" text="dark">
            <Spinner animation="border" size="sm" className="me-1" /> Cargando
          </Badge>
        ) : (
          <Badge
            bg={useFallback ? "secondary" : "warning"}
            text={useFallback ? undefined : "dark"}
            title="Ver reseñas"
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => !useFallback && total > 0 ? openModal() : null}
          >
            {badgeText}
          </Badge>
        )}
      </div>

      {/* Modal de reseñas */}
      <Modal show={show} onHide={() => setShow(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Reseñas ({total}) · {displayAvg?.toFixed(1)}★
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {items.length === 0 && !listLoading ? (
            <div className="text-muted">Este beneficio todavía no tiene reseñas.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {items.map((it) => (
                <div key={it.id} className="p-2 border rounded">
                  <div className="d-flex align-items-center justify-content-between">
                    <strong>{"★".repeat(it.rating)}{"☆".repeat(5 - it.rating)}</strong>
                    <small className="text-muted">
                      {new Date(it.created_at).toLocaleDateString("es-AR", { year: "numeric", month: "short", day: "numeric" })}
                    </small>
                  </div>
                  {renderReviewer(it)}
                  {it.comment && <div className="mt-1">{it.comment}</div>}
                </div>
              ))}
              {listLoading && (
                <div className="text-center">
                  <Spinner animation="border" size="sm" /> Cargando…
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>Cerrar</Button>
          <Button variant="primary" onClick={loadMore} disabled={listLoading || listEnd}>
            {listEnd ? "No hay más" : "Cargar más"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
